import AddAccountButton from '@/components/accounts/AddAccountButton';
import { showLongFetchTimeModal } from '@/components/accounts/LongFetchTimeModal';
import { FlexRow } from '@/components/common/Flex';
import { LongFetchTimeIndicator } from '@/components/common/LongFetchTimeIndicator';
import {
    AccountListItemProps,
    getAccountListItemProps,
} from '@/services/accounts/getAccountListItemProps';
import { useTracking } from '@/services/analytics/useTracking';
import { toDollarsWithCents } from '@/util/currency';
import {
    Box,
    Divider,
    FlexProps,
    MenuItem,
    Skeleton,
    Spinner,
    StackDivider,
    VStack,
} from '@chakra-ui/react';
import {
    FinancialCategory,
    isExternalFinancialEntity,
} from '@common/financialEntities';
import { Account, useGroupedAccounts } from '@common/react/accounts';
import { useShowLongFetchTimeIndicator } from '@common/react/accounts/useShowLongFetchTimeIndicator';
import { useFinancialEntity } from '@common/react/financialEntities/context';
import { useCurrentUser } from '@common/react/user';
import { useCallback } from 'react';
import useLocalStorageState from 'use-local-storage-state';
import ActionMenu from '../common/ActionMenu';
import { MenuButton } from '../common/Button';
import { useDrawer } from '../common/DrawerManager';
import FadeInBox from '../common/FadeInBox';
import { FlexCol } from '../common/Flex';
import { Minus, Plus } from '../common/icons';
import { Skeletons } from '../common/Skeletons';
import SquareIconButton from '../common/SquareIconButton';
import { groupHoverStyleProps, hoverStyleProps } from '../common/styleHelpers';
import { EyebrowHeader, Subheader, Text } from '../common/Typography';
import { startFinancialEntityAddFlow } from '../financialEntities/FinancialEntityAddFlow';

type AccountGroup = {
    key: FinancialCategory;
    label: string;
    emptyStateText: string;
    accounts: Account[];
    total: number;
};

const ALL_GROUP_IDS: FinancialCategory[] = [
    'CashAccount',
    'Investment',
    'Debt',
    'CreditCard',
    'Retirement',
    'OtherAsset',
];

const DashboardAccounts = (props: FlexProps) => {
    const { showDrawer } = useDrawer();
    const { track } = useTracking();
    const { groupedAccounts, loading } = useGroupedAccounts({
        returnSampleDataIfEmpty: true,
    });
    const [openedIds, setOpenedIds] = useLocalStorageState<FinancialCategory[]>(
        'opened-dashboard-account-groups',
        {
            defaultValue: groupedAccounts.map((g) => g.key),
        }
    );
    const toggleOpened = useCallback(
        (key: FinancialCategory) => {
            const openedIdsSet = new Set(openedIds);
            openedIdsSet.has(key)
                ? openedIdsSet.delete(key)
                : openedIdsSet.add(key);
            setOpenedIds(Array.from(openedIdsSet));
        },
        [openedIds, setOpenedIds]
    );

    return (
        <FlexCol w="full" {...props}>
            <FlexRow w="full" justify="space-between" align="center">
                <Subheader>Accounts</Subheader>

                <FlexRow gap={2} align="center">
                    <Box pos="relative">
                        <ActionMenu
                            menuButton={
                                <MenuButton
                                    as={SquareIconButton}
                                    iconName="FilterLines"
                                    onClickEvent="Filter Accounts Menu Button Clicked"
                                />
                            }
                            placement="bottom-start"
                        >
                            <MenuItem
                                icon={<Plus size="sm" />}
                                onClick={() => {
                                    track('Filter Accounts Expand All Clicked');
                                    setOpenedIds(ALL_GROUP_IDS);
                                }}
                            >
                                <Text fontSize="md">Expand all</Text>
                            </MenuItem>
                            <MenuItem
                                icon={<Minus size="sm" />}
                                onClick={() => {
                                    track(
                                        'Filter Accounts Collapse All Clicked'
                                    );
                                    setOpenedIds([]);
                                }}
                            >
                                <Text fontSize="md">Collapse all</Text>
                            </MenuItem>
                        </ActionMenu>
                    </Box>
                    <AddAccountButton
                        h="28px"
                        w="140px"
                        onClick={() =>
                            startFinancialEntityAddFlow({
                                onCompleteManual: (feId) => {
                                    showDrawer({
                                        drawerKey: 'financial-entity-detail',
                                        entityId: feId,
                                    });
                                },
                            })
                        }
                        onClickEventParams={{
                            location: 'Dashboard',
                        }}
                    />
                </FlexRow>
            </FlexRow>
            <Divider mt={4} />
            {loading && (
                <>
                    <Skeleton h={6} w={24} mt={6} mb={2} />
                    <Skeletons count={3} w="80%" h={4} mb={2} />
                    <Skeleton h={6} w={24} mt={6} mb={2} />
                    <Skeletons count={2} w="80%" h={4} mb={2} />
                    <Skeleton h={6} w={24} mt={6} mb={2} />
                    <Skeletons count={4} w="80%" h={4} mb={2} />
                </>
            )}
            {!loading &&
                groupedAccounts.map((group) => (
                    <DashboardAccountGroup
                        key={group.key}
                        group={group}
                        isOpen={openedIds.includes(group.key)}
                        onToggle={toggleOpened}
                    />
                ))}
        </FlexCol>
    );
};

interface DashboardAccountGroupProps extends Omit<FlexProps, 'onToggle'> {
    group: AccountGroup;
    isOpen: boolean;
    onToggle: (key: FinancialCategory) => void;
}
const DashboardAccountGroup = ({
    group,
    isOpen,
    onToggle,
    ...props
}: DashboardAccountGroupProps) => {
    const { userInfo } = useCurrentUser();
    const { showDrawer } = useDrawer();
    return (
        <FlexCol w="full" {...props}>
            <Box
                {...itemHoverStyleProps}
                rounded={2}
                data-cy={`account-group-${group.key}`}
                onClick={() => onToggle(group.key)}
            >
                <FlexRow w="full" align="center" h={18}>
                    <FlexRow
                        role="group"
                        {...hoverStyleProps({})}
                        gap={4}
                        grow={1}
                        align="center"
                    >
                        {isOpen ? (
                            <Minus size="sm" color="blue.500" />
                        ) : (
                            <Plus size="sm" color="blue.500" />
                        )}
                        <EyebrowHeader
                            color="text"
                            mb={0}
                            transition={'200ms all'}
                            {...groupHoverStyleProps({ color: 'blue.500' })}
                        >
                            {group.label}
                        </EyebrowHeader>
                    </FlexRow>
                    <FlexRow grow={0}>
                        <Text fontSize="sm" fontWeight="semibold">
                            {toDollarsWithCents(group.total)}
                        </Text>
                    </FlexRow>
                </FlexRow>
            </Box>
            <Divider />
            {isOpen && (
                <FadeInBox mb={4}>
                    <VStack
                        spacing={0}
                        divider={<StackDivider borderColor="gray.100" />}
                        align="stretch"
                    >
                        {group.accounts.map((account) => {
                            const props = getAccountListItemProps({
                                account,
                                showDrawer,
                                userInfo,
                            });
                            return (
                                <AccountListItem key={account.id} {...props} />
                            );
                        })}
                        {group.accounts.length === 0 && (
                            <FlexRow
                                px={4}
                                py={6}
                                bg="gray.100"
                                borderBottomRadius="md"
                                color="gray.400"
                                justify="space-between"
                                align="center"
                                {...hoverStyleProps({
                                    color: 'blue.400',
                                })}
                                onClick={() => {
                                    startFinancialEntityAddFlow({
                                        onCompleteManual: (feId) => {
                                            showDrawer({
                                                drawerKey:
                                                    'financial-entity-detail',
                                                entityId: feId,
                                            });
                                        },
                                    });
                                }}
                            >
                                <Text fontSize="sm">
                                    {group.emptyStateText}
                                </Text>
                                <Plus size="sm" />
                            </FlexRow>
                        )}
                    </VStack>
                </FadeInBox>
            )}
        </FlexCol>
    );
};

const AccountListItem = ({
    id,
    icon,
    title,
    subtitle,
    isLinkingInProgress,
    lastUpdatedISO,
    value,
    right,
    onClick,
}: AccountListItemProps) => {
    const { track } = useTracking();
    const showLongFetchTimeIndicator = useShowLongFetchTimeIndicator({
        isLinkingInProgress,
        lastUpdatedISO,
    });

    const financialEntity = useFinancialEntity({ id })?.fe;
    const externalFinancialEntity =
        financialEntity && isExternalFinancialEntity(financialEntity)
            ? financialEntity
            : undefined;

    return (
        <Box
            {...(isLinkingInProgress || !onClick ? {} : itemHoverStyleProps)}
            rounded="md"
            onClick={
                showLongFetchTimeIndicator
                    ? () => {
                          track('Long Fetch Time Modal Viewed', {
                              institution:
                                  externalFinancialEntity?.institution?.name,
                          });
                          showLongFetchTimeModal(externalFinancialEntity);
                      }
                    : onClick
            }
        >
            <FlexRow
                h={18}
                align="center"
                justify="space-between"
                role="group"
                gap={4}
                {...hoverStyleProps({})}
            >
                <FlexRow flexGrow={1} overflow="hidden">
                    <Box mt={0.5}>{icon}</Box>
                    <FlexCol justifyContent="center" ml={4} maxW={80} minW={0}>
                        <Text
                            overflow="hidden"
                            whiteSpace="nowrap"
                            textOverflow="ellipsis"
                        >
                            {title}
                        </Text>
                        <Text
                            color="textMuted"
                            fontSize="sm"
                            overflow="hidden"
                            whiteSpace="nowrap"
                            textOverflow="ellipsis"
                            fontFamily="mono"
                        >
                            {subtitle}
                        </Text>
                    </FlexCol>
                </FlexRow>

                <FlexCol textAlign="right" justify="center">
                    {isLinkingInProgress ? (
                        showLongFetchTimeIndicator ? (
                            <LongFetchTimeIndicator />
                        ) : (
                            <Spinner speed="1s" color="gray.400" />
                        )
                    ) : right ? (
                        right
                    ) : (
                        <>
                            <Text fontSize="sm" fontWeight="medium">
                                {toDollarsWithCents(value || 0)}
                            </Text>
                        </>
                    )}
                </FlexCol>
            </FlexRow>
        </Box>
    );
};

const itemHoverStyleProps = hoverStyleProps({
    mx: -3,
    px: 3,
    bg: 'gray.100',
});

export default DashboardAccounts;
